import React, { useCallback } from 'react'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { t, Trans } from 'src/translations'
import { useLocationSearchError } from 'src/shared/components/common/location_search/LocationSearchErrorContext'

type LocationSearchErrorProps = {
  setShowSearchLabel?: (show: boolean) => void
  noNearbyLocations?: boolean
  shouldShowAllLocations?: boolean
  setShouldShowAllLocations?: (show: boolean) => void
  diningOptionBehavior?: DiningOptionBehavior
  hasCustomerLocation?: boolean
  hasSearched?: boolean
}
export const LocationSearchError = ({
  setShouldShowAllLocations,
  setShowSearchLabel,
  shouldShowAllLocations,
  noNearbyLocations,
  diningOptionBehavior,
  hasCustomerLocation,
  hasSearched
}: LocationSearchErrorProps) => {
  const { currentLocationError, setCurrentLocationError } =
    useLocationSearchError()
  const showAllLocations = useCallback(() => {
    setShouldShowAllLocations && setShouldShowAllLocations(true)
    setCurrentLocationError(undefined)
  }, [setCurrentLocationError, setShouldShowAllLocations])

  if (
    !hasSearched &&
    !shouldShowAllLocations &&
    !currentLocationError &&
    setShouldShowAllLocations
  ) {
    setShowSearchLabel && setShowSearchLabel(false)
    return (
      <div
        data-testid='places-autocomplete-error'
        className='placesAutocompleteError'
      >
        <p>
          {t('location-selection.enter-address')}{' '}
          <button onClick={showAllLocations}>
            {t('location-selection.click-here')}
          </button>{' '}
          {t('location-selection.browse-all-locations')}
        </p>
      </div>
    )
  }
  if (currentLocationError === 'POSITION_UNAVAILABLE') {
    setShowSearchLabel && setShowSearchLabel(false)
    return (
      <div className='placesAutocompleteError'>
        <span>{t('location-search-error.we-couldnt-locate-you')}</span>
        <p>
          {setShouldShowAllLocations ? (
            <>
              <Trans
                i18nKey='location-search-error.or-click-here'
                components={{ button: <button onClick={showAllLocations} /> }}
              />
            </>
          ) : (
            t(
              'location-search-error.please-enter-address-or-enable-location-access'
            )
          )}
        </p>
      </div>
    )
  }

  if (
    diningOptionBehavior === DiningOptionBehavior.Delivery &&
    !shouldShowAllLocations &&
    !noNearbyLocations &&
    hasCustomerLocation &&
    !currentLocationError
  ) {
    setShowSearchLabel && setShowSearchLabel(false)
    return (
      <div className='placesAutocompleteError'>
        <span>
          {t(
            'location-search-error.full-address-required-to-check-delivery-options'
          )}
        </span>
        <p>
          <Trans
            i18nKey='location-search-error.try-searching-specific-address-or'
            components={{ button: <button onClick={showAllLocations} /> }}
          />
        </p>
      </div>
    )
  }
  if (hasSearched && noNearbyLocations && !shouldShowAllLocations) {
    setShowSearchLabel && setShowSearchLabel(false)
    return (
      <div className='placesAutocompleteError'>
        <span>{t('location-search-error.no-locations-nearby')}</span>
        <p>
          {setShouldShowAllLocations ? (
            <>
              <Trans
                i18nKey='location-search-error.try-searching-different-area-or'
                components={{ button: <button onClick={showAllLocations} /> }}
              />
            </>
          ) : (
            t('location-search-error.try-searching-different-area')
          )}
        </p>
      </div>
    )
  }

  return currentLocationError ? (
    <div className='placesAutocompleteError'>{currentLocationError}</div>
  ) : null
}
